import {
  CarAxleType,
  CarAxleTypeLabels,
  CarWheelPositionText,
  InspectionItemKind,
  type CarWheelSetCatalogRecord,
  type CatalogWheelPackageRecord
} from '@navio/nsdk';
import { useStore } from '@tanstack/react-form';
import {
  invalidateInspectionQueries,
  invalidateQuery,
  invalidateQueryExactMatch,
  noddiAsync,
  URLKeys
} from 'noddi-async';
import { ApiErrorMessageInternal, NoddiInfoBanner, NoddiSelectableTile, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { CatalogPackageFitments } from './CatalogPackageFitments';
import { CatalogWheelPackageMarkers } from './CatalogWheelPackageMarkers';
import { catalogWheelPackageRows } from './catalogWheelPackageRows';

const FORM_ID = 'map-wheel-set-package';
const AUTOMATIC = 0;

type Props = {
  carId: number;
  wheelSet: CarWheelSetCatalogRecord;
  packages: { trimName: string; package: CatalogWheelPackageRecord }[];
  onClose: () => void;
};

const schema = z.object({ packageId: z.number().int().nonnegative() });

/**
 * Choose the catalog package a wheel set runs on. A package whose sizes differ from the registered
 * ones also corrects those sizes: staff pick a package because the customer told them what is on the
 * car, so the registered sizes are what is wrong, never the choice.
 */
export const MapWheelSetPackageSheet = ({ carId, wheelSet, packages, onClose }: Props) => {
  const refresh = () =>
    Promise.all([
      invalidateQueryExactMatch({ urlKey: URLKeys.getCar, input: { carId } }),
      invalidateQuery({ urlKey: URLKeys.getCarWheelSet }),
      invalidateQueryExactMatch({ urlKey: URLKeys.getCarWheelSetsForCar, input: { carId } }),
      invalidateInspectionQueries()
    ]);
  const { mutateAsync: recordSize, error: sizeError } = noddiAsync.usePost({
    type: URLKeys.postCarInspectionDimensions,
    queryConfig: { meta: { suppressErrorToast: true, suppressSuccessToast: true }, onSuccess: refresh }
  });
  const { mutateAsync: mapPackage, error: mapError } = noddiAsync.usePatch({
    type: URLKeys.patchCarWheelSet,
    queryConfig: { meta: { suppressErrorToast: true }, onSuccess: refresh }
  });
  const defaultValues: z.input<typeof schema> = { packageId: wheelSet.catalogMapping?.optionId ?? AUTOMATIC };
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      const selected = packages.find((item) => item.package.id === value.packageId)?.package;
      // Sizes first: the backend links a package only when it matches the registered sizes.
      for (const change of selected ? sizeChanges(wheelSet, selected) : []) {
        for (const location of AXLE_WHEELS[change.axleType] ?? []) {
          await recordSize({
            carId,
            context: 'STANDALONE',
            kind: InspectionItemKind.SPEC,
            location,
            tireSeason: wheelSet.type.value,
            tireSpecification: change.to
          });
        }
      }
      await mapPackage(
        { pk: wheelSet.id, catalogWheelOptionId: value.packageId === AUTOMATIC ? null : value.packageId },
        { onSuccess: onClose }
      );
    }
  });
  const packageId = useStore(form.store, (state) => state.values.packageId);
  const selected = packages.find((item) => item.package.id === packageId);
  const changes = selected ? sizeChanges(wheelSet, selected.package) : [];
  const unsupported = changes.find((change) => AXLE_WHEELS[change.axleType] == null);
  const rows = catalogWheelPackageRows(packages.map((item) => item.package));
  const automatic = wheelSet.automaticPackageMapping?.package;
  const error = sizeError ?? mapError;

  return (
    <form.AppForm>
      <NoddiSheet
        open
        onOpenChange={(open) => !open && !form.state.isSubmitting && onClose()}
        title={`Review ${wheelSet.type.label.toLowerCase()} specifications`}
        description='Choose the wheel package on the car. A package with other sizes also updates the registered sizes.'
        footer={
          <form.SubscribeButton
            formId={FORM_ID}
            label={changes.length > 0 ? 'Update sizes and save' : 'Save'}
            disabledReason={
              unsupported
                ? `The ${CarAxleTypeLabels[unsupported.axleType].toLowerCase()} axle size cannot be changed here.`
                : undefined
            }
          />
        }
      >
        <form
          id={FORM_ID}
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
          className='flex flex-col gap-4'
        >
          <div role='group' aria-label='Wheel package' className='flex flex-col gap-3'>
            <NoddiSelectableTile
              size='small'
              selected={packageId === AUTOMATIC}
              onClick={() => form.setFieldValue('packageId', AUTOMATIC)}
            >
              <div className='flex flex-col gap-1 pr-6'>
                <span className='text-sm font-semibold'>Automatic matching</span>
                <span className='text-xs text-muted-foreground'>
                  {automatic
                    ? `Uses package #${automatic.id}, which matches the registered sizes.`
                    : 'No package matches the registered sizes, so none applies.'}
                </span>
              </div>
            </NoddiSelectableTile>
            {rows.map((row, index) => (
              <NoddiSelectableTile
                key={row.package.id}
                size='small'
                selected={packageId === row.package.id}
                onClick={() => form.setFieldValue('packageId', row.package.id)}
              >
                <div className='flex w-full flex-col gap-2 pr-6'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-sm font-semibold'>
                      {packages[index]?.trimName} · #{row.package.id}
                    </span>
                    <CatalogWheelPackageMarkers wheelPackage={row.package} plain />
                  </div>
                  <CatalogPackageFitments fitments={row.fitments} kind='tire' labelAxles plain />
                </div>
              </NoddiSelectableTile>
            ))}
          </div>
          {changes.length > 0 && (
            <NoddiInfoBanner
              variant='warning'
              title='Saving also changes the registered sizes'
              texts={changes.map((change) => ({
                key: CarAxleTypeLabels[change.axleType],
                value: `${change.from ?? 'Not registered'} → ${change.to}`
              }))}
            />
          )}
          {error && (
            <div role='alert'>
              <ApiErrorMessageInternal error={error} />
            </div>
          )}
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};

type SizeChange = { axleType: CarAxleType; from: string | null; to: string };

/** Wheels a size is recorded on. Other axles have no wheel set positions to record against yet. */
const AXLE_WHEELS: Partial<Record<CarAxleType, CarWheelPositionText[]>> = {
  [CarAxleType.FRONT]: [CarWheelPositionText.FRONT_LEFT, CarWheelPositionText.FRONT_RIGHT],
  [CarAxleType.REAR]: [CarWheelPositionText.REAR_LEFT, CarWheelPositionText.REAR_RIGHT]
};

/** Axles whose registered size differs from the package. An axle the package leaves unreported keeps its size. */
const sizeChanges = (wheelSet: CarWheelSetCatalogRecord, wheelPackage: CatalogWheelPackageRecord): SizeChange[] =>
  wheelPackage.axles.flatMap((axle) => {
    if (axle.tireSpecification == null) {
      return [];
    }
    const registered =
      (wheelSet.axles ?? []).find((entry) => entry.type === axle.axleType)?.tireSpecificationStr ?? null;
    return registered === axle.tireSpecification
      ? []
      : [{ axleType: axle.axleType, from: registered, to: axle.tireSpecification }];
  });
